import { t, getLang, type StringKey } from './i18n';
import { icon } from './icons';
import { words } from './words';
import type { Hint } from '../tracking/TrackingStateMachine';

/**
 * Minimal, brand-neutral DOM for the try-on modal: white, vertical, no emoji.
 * Everything is built programmatically so the embed script is self-contained.
 */
export interface OverlayElements {
  root: HTMLElement;
  stage: HTMLElement;
  video: HTMLVideoElement;
  /** Frame-synchronised copy of the video (shown instead of the <video> when the app locks display and tracking to the same frame). */
  videoCanvas: HTMLCanvasElement;
  canvas: HTMLCanvasElement;
  debugCanvas: HTMLCanvasElement;
  hint: HTMLElement;
  /** A110X: « L’essayage s’est figé… » + « Relancer l’essayage ». */
  recover: HTMLElement;
  recoverBtn: HTMLButtonElement;
  /** A110Y: the message and the button label change with the problem (relaunch, or reload the page). */
  recoverText: HTMLElement;
  recoverLabel: HTMLElement;
  product: HTMLElement;
  closeBtn: HTMLButtonElement;
  /** A42E: « ? » button that toggles the help popover. */
  helpBtn: HTMLButtonElement;
  consent: HTMLElement;
  allowBtn: HTMLButtonElement;
  cancelBtn: HTMLButtonElement;
  loading: HTMLElement;
  error: HTMLElement;
  errorText: HTMLElement;
  retryBtn: HTMLButtonElement;
  debugHost: HTMLElement;
}

export function buildOverlay(container: HTMLElement, productLabel: string, mirrored: boolean): OverlayElements {
  container.classList.add('mt-root', 'mt-ui');
  container.lang = getLang();
  container.dir = getLang() === 'ar' ? 'rtl' : 'ltr';
  container.innerHTML = '';

  const stage = el('div', 'mt-stage');
  const video = document.createElement('video');
  video.className = 'mt-video';
  const videoCanvas = document.createElement('canvas');
  videoCanvas.className = 'mt-video-canvas';
  videoCanvas.hidden = true;
  const canvas = document.createElement('canvas');
  canvas.className = 'mt-canvas';
  const debugCanvas = document.createElement('canvas');
  debugCanvas.className = 'mt-debug-canvas';
  debugCanvas.hidden = true;
  if (mirrored) {
    video.classList.add('mt-mirror');
    videoCanvas.classList.add('mt-mirror');
    canvas.classList.add('mt-mirror');
    debugCanvas.classList.add('mt-mirror');
  }

  // A42E: glass title chip (top left), close and help (top right).
  const topbar = el('div', 'mt-topbar');
  const product = el('span', 'mt-product', productLabel);
  const actions = el('div', 'mt-top-actions');
  const closeBtn = document.createElement('button');
  closeBtn.className = 'mt-close mt-glass-btn';
  closeBtn.type = 'button';
  closeBtn.setAttribute('aria-label', t('close'));
  closeBtn.append(icon('close'));
  const helpBtn = document.createElement('button');
  helpBtn.className = 'mt-help-btn mt-glass-btn';
  helpBtn.type = 'button';
  helpBtn.setAttribute('aria-label', words().help);
  helpBtn.append(icon('help'));
  actions.append(closeBtn, helpBtn);
  topbar.append(product, actions);

  const hint = el('div', 'mt-hint');
  hint.hidden = true;

  // A110X: shown only when the try-on did not recover on its own after the phone slept.
  const recover = el('div', 'mt-recover');
  recover.setAttribute('role', 'alert');
  const recoverText = el('p', 'mt-recover-text', words().recoverText);
  const recoverBtn = button('mt-recover-btn', '');
  const recoverLabel = el('span', 'mt-recover-label', words().recoverButton);
  recoverBtn.append(icon('reset', 16), recoverLabel);
  recover.append(recoverText, recoverBtn);
  recover.hidden = true;

  stage.append(video, videoCanvas, canvas, debugCanvas, topbar, hint, recover);

  // Consent
  const consent = el('div', 'mt-panel mt-consent');
  const consentTitle = el('h2', 'mt-title', t('consent_title'));
  const consentBody = el('p', 'mt-body', t('consent_body'));
  const allowBtn = button('mt-btn mt-btn-primary', t('consent_allow'));
  const cancelBtn = button('mt-btn', t('consent_cancel'));
  consent.append(consentTitle, consentBody, allowBtn, cancelBtn);

  // Loading
  const loading = el('div', 'mt-panel mt-loading');
  loading.append(el('p', 'mt-body', t('loading')));
  loading.hidden = true;

  // Error
  const error = el('div', 'mt-panel mt-error');
  const errorText = el('p', 'mt-body');
  const retryBtn = button('mt-btn mt-btn-primary', t('retry'));
  error.append(errorText, retryBtn);
  error.hidden = true;

  const debugHost = el('div', 'mt-debug');
  debugHost.hidden = true;

  container.append(stage, consent, loading, error, debugHost);

  return { root: container, stage, video, videoCanvas, canvas, debugCanvas, hint, recover, recoverBtn, recoverText, recoverLabel, product, closeBtn, helpBtn, consent, allowBtn, cancelBtn, loading, error, errorText, retryBtn, debugHost };
}

export function showHint(ui: OverlayElements, hint: Hint): void {
  if (hint === 'none') {
    ui.hint.hidden = true;
    return;
  }
  const key = `hint_${hint}` as StringKey;
  ui.hint.textContent = t(key);
  ui.hint.hidden = false;
}

export function showError(ui: OverlayElements, key: StringKey, canRetry = true): void {
  ui.errorText.textContent = t(key);
  ui.retryBtn.hidden = !canRetry;
  ui.error.hidden = false;
  ui.loading.hidden = true;
  ui.consent.hidden = true;
}

function el(tag: string, className: string, text?: string): HTMLElement {
  const e = document.createElement(tag);
  e.className = className;
  if (text !== undefined) e.textContent = text;
  return e;
}

function button(className: string, text: string): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = className;
  b.textContent = text;
  return b;
}
